
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

// ==========================================
// SESSION TRACKING
// ==========================================
const SESSION_ID = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

// ==========================================
// MODULE NAME MAPPING
// ==========================================
const MODULE_MAP = {
    'home': 'Home Page',
    'login': 'Login Page',
    'register': 'Student Registration',
    'student-registration': 'Student Registration',
    'student-dashboard': 'Student Dashboard',
    'hostel-superadmin-dashboard': 'Superadmin Dashboard',
    'hostel-superadmin-manage-hostels': 'Manage Hostels',
    'hostel-superadmin-manage-application': 'Manage Applications',
    'hostel-superadmin-manage-students': 'Manage Students',
    'hostel-superadmin-manage-rooms': 'Manage Rooms',
    'hostel-superadmin-manage-complaints': 'Manage Complaints',
    'hostel-superadmin-settings': 'Superadmin Settings',
    'hostel-admin-dashboard': 'Hostel Admin Dashboard',
    'hostel-admin-manage-students': 'Hostel Admin - Students',
    'hostel-admin-manage-application': 'Hostel Admin - Applications',
    'hostel-admin-manage-rooms': 'Hostel Admin - Rooms',
    'hostel-admin-manage-complaints': 'Hostel Admin - Complaints',
    'mess-manager-dashboard': 'Mess Manager Dashboard',
    'mess-manager-menu': 'Mess Manager - Menu',
    'mess-manager-feedback': 'Mess Manager - Feedback',
    'forgot-password': 'Forgot Password',
    'application-status': 'Application Status'
};

/**
 * Get a human-readable module name from a page key
 */
export const getModuleName = (pageKey) => {
    return MODULE_MAP[pageKey] || pageKey || 'Unknown';
};

// ==========================================
// DEBOUNCE - prevent flooding for rapid clicks
// ==========================================
let pendingLogs = [];
let flushTimer = null;

const flushLogs = async () => {
    if (pendingLogs.length === 0) return;

    const logsToSend = [...pendingLogs];
    pendingLogs = [];

    try {
        const userInfo = JSON.parse(
            sessionStorage.getItem('userInfo') ||
            localStorage.getItem('userInfo') ||
            '{}'
        );

        if (!userInfo.userId && !userInfo.id) return;

        await fetch(`${API_BASE_URL}/log-activity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: userInfo.userId || userInfo.id,
                userEmail: userInfo.email || null,
                userRole: userInfo.role || 'unknown',
                sessionId: SESSION_ID,
                action: 'BATCH_ACTIVITY',
                details: {
                    events: logsToSend,
                    batchSize: logsToSend.length,
                    timestamp: new Date().toISOString()
                }
            })
        });
    } catch (error) {
        // Silently fail
    }
};

/**
 * Log a single frontend activity event.
 * Events are batched and sent every 5 seconds to avoid flooding.
 *
 * @param {string} action - Action type: 'PAGE_VIEW', 'CLICK', 'MODULE_ENTER', 'MODULE_EXIT'
 * @param {object} details - Additional context
 */
export const logActivity = async (action, details = {}) => {
    try {
        const userInfo = JSON.parse(
            sessionStorage.getItem('userInfo') ||
            localStorage.getItem('userInfo') ||
            '{}'
        );

        const uid = userInfo.userId || userInfo.id;
        if (!uid) return;

        // For high-priority actions (PAGE_VIEW, MODULE_ENTER), send immediately
        if (action === 'PAGE_VIEW' || action === 'MODULE_ENTER' || action === 'MODULE_EXIT') {
            await fetch(`${API_BASE_URL}/log-activity`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: uid,
                    userEmail: userInfo.email || null,
                    userRole: userInfo.role || 'unknown',
                    sessionId: SESSION_ID,
                    action,
                    details: {
                        ...details,
                        moduleName: getModuleName(details.page),
                        url: window.location.href,
                        timestamp: new Date().toISOString()
                    }
                })
            });
            return;
        }

        // For CLICK events, batch them
        pendingLogs.push({
            action,
            ...details,
            moduleName: getModuleName(details.page),
            url: window.location.href,
            timestamp: new Date().toISOString()
        });

        // Flush every 5 seconds
        if (!flushTimer) {
            flushTimer = setTimeout(() => {
                flushLogs();
                flushTimer = null;
            }, 5000);
        }

        // Also flush if batch gets too large
        if (pendingLogs.length >= 20) {
            clearTimeout(flushTimer);
            flushTimer = null;
            flushLogs();
        }

    } catch (error) {
        // Silently fail
    }
};

/**
 * Track time spent on a module. Call on module enter, returns a function to call on exit.
 */
let moduleEnterTime = null;
let currentModuleKey = null;

export const trackModuleEnter = (pageKey) => {
    // If leaving a previous module, log exit with time spent
    if (currentModuleKey && moduleEnterTime) {
        const timeSpentMs = Date.now() - moduleEnterTime;
        logActivity('MODULE_EXIT', {
            page: currentModuleKey,
            moduleName: getModuleName(currentModuleKey),
            timeSpentSeconds: Math.round(timeSpentMs / 1000)
        });
    }

    currentModuleKey = pageKey;
    moduleEnterTime = Date.now();

    logActivity('MODULE_ENTER', {
        page: pageKey,
        moduleName: getModuleName(pageKey)
    });
};

// Flush remaining logs when user leaves the page
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        // Log module exit
        if (currentModuleKey && moduleEnterTime) {
            const timeSpentMs = Date.now() - moduleEnterTime;
            const userInfo = JSON.parse(
                sessionStorage.getItem('userInfo') ||
                localStorage.getItem('userInfo') ||
                '{}'
            );
            const uid = userInfo.userId || userInfo.id;
            if (uid) {
                navigator.sendBeacon(`${API_BASE_URL}/log-activity`, JSON.stringify({
                    userId: uid,
                    userRole: userInfo.role || 'unknown',
                    sessionId: SESSION_ID,
                    action: 'MODULE_EXIT',
                    details: {
                        page: currentModuleKey,
                        moduleName: getModuleName(currentModuleKey),
                        timeSpentSeconds: Math.round(timeSpentMs / 1000),
                        reason: 'page_unload'
                    }
                }));
            }
        }

        // Flush any remaining click logs
        if (pendingLogs.length > 0) {
            const userInfo = JSON.parse(
                sessionStorage.getItem('userInfo') ||
                localStorage.getItem('userInfo') ||
                '{}'
            );
            const uid = userInfo.userId || userInfo.id;
            if (uid) {
                navigator.sendBeacon(`${API_BASE_URL}/log-activity`, JSON.stringify({
                    userId: uid,
                    userRole: userInfo.role || 'unknown',
                    sessionId: SESSION_ID,
                    action: 'BATCH_ACTIVITY',
                    details: { events: pendingLogs, batchSize: pendingLogs.length }
                }));
            }
        }
    });
}
